import { Async } from '@client/new/helpers/types';
import { useInfluencer } from '@client/new/hooks/use-influencer';
import { isAfterOrSameDay } from '@client/new/pages/promotions/view-model/utils';

import { OtpEarningsBreakdown, useOtpPayouts } from './useOtpPayouts';
import {
  EarningsBreakdown,
  useSubscriptionPayouts,
} from './useSubscriptionPayouts';

export type usePayoutsProps = {
  date: Date;
  creatorId: number;
};

export type PayoutsBreakdown = {
  subscription: EarningsBreakdown;
  otp: OtpEarningsBreakdown;
  exportEnabled: boolean;
};

export const usePayouts = ({
  date,
  creatorId,
}: usePayoutsProps): Async<PayoutsBreakdown> => {
  const subscriptionPayouts = useSubscriptionPayouts({ date, creatorId });
  const otpPayouts = useOtpPayouts({ date, creatorId });
  const {
    isLoading: isInfluencerLoading,
    influencer,
    error: influencerError,
  } = useInfluencer({ creatorId });

  if (
    subscriptionPayouts.type === 'fetch-error' ||
    otpPayouts.type === 'fetch-error' ||
    !!influencerError
  ) {
    return {
      type: 'fetch-error',
      value: 'could not fetch data',
    };
  }

  if (
    !isInfluencerLoading &&
    !!influencer &&
    subscriptionPayouts.type === 'fetched' &&
    otpPayouts.type === 'fetched'
  ) {
    const hasBeenPaid =
      import.meta.env.VITE_ENV === 'production'
        ? !!otpPayouts.value.paidAt && !!subscriptionPayouts.value.paidAt
        : true;

    const isTransactionLedgerEnabled =
      influencer.customizations?.isTransactionLedgerEnabled ?? false;

    return {
      type: 'fetched',
      value: {
        subscription: subscriptionPayouts.value,
        otp: otpPayouts.value,
        exportEnabled:
          isTransactionLedgerEnabled &&
          hasBeenPaid &&
          isAfterOrSameDay(date, new Date('2025-05-01')),
      },
    };
  }

  return {
    type: 'fetching',
  };
};
